'use client'

import { useRouter } from 'next/router'

import type { PLPageMeta } from '@src/components/pl-page-context'
import { ProductPagePLRenderer } from '@src/pages/pdp1/product-page-pl-renderer'
import { getBundlePageContext } from '@src/quince-site-pdp/src/domains/BundlePageContext'
import { getProductPageContext, ProductPageBFFResponse } from '@src/quince-site-pdp/src/domains/ProductPageContext'
import BundlePage from '@src/quince-site-pdp/src/templates/bundlePage/BundlePage'
import { ProductPage } from '@src/quince-site-pdp/src/templates/productPage/index'
import type { PLAPIResponse } from '@src/server-side/api/fetch-pl-data'
import { FEATURE_FLAG_TYPE } from '@src/server-side/server-feature-flags/feature-flag.types'

import { ProductFeatureProvider } from '../components/product/productFeatureProvider'

import { PageTemplateProps } from './page-templates.types'

interface ProductAndBundlePageData {
  readonly context: PageTemplateProps['pageData']['context'] & {
    readonly isPLFormat?: boolean
  }
}

/**
 * Build a minimal PLPageMeta for the PDP PL renderer from template props.
 *
 * `searchParams` is the page URL query string (color / size / gender / …), derived by the caller
 * from `useRouter().query` minus the `[...slug]` catch-all. It flows into the client layout /
 * lazy-widget fetches so paginated + full-reload PL requests carry the same variant context as the
 * SSR initial load (`product-page-data-pl.ts`), which the shopper's selection depends on.
 *
 * `platformType` is intentionally left unset: the PL fetch utilities resolve it via client-side
 * detection (`getPlatformType` → native/mobile/tablet/desktop), which is finer-grained than the
 * coarse `isMobile` prop, so plumbing that boolean would be a downgrade. This matches the CLP template.
 */
function buildPageMetaForPL(
  plData: PLAPIResponse,
  pathname: string,
  searchParams: string,
  zipCode?: string | null,
): PLPageMeta {
  const metaSlug = plData.meta?.slug ?? pathname
  let seoData: PLPageMeta['seoData'] = null
  if (plData.meta?.seoData) {
    seoData = {
      ...plData.meta.seoData,
      slug: metaSlug,
    }
  }

  return {
    title: plData.meta?.title ?? null,
    pathname,
    searchParams,
    seoData,
    zipCode: zipCode ?? null,
    context: plData.context,
    // Drives the v2 lazy-batch `pageType` (→ PDP) and observability tagging.
    // eslint-disable-next-line i18next/no-literal-string -- page-type identifier, not user copy
    pageType: 'pdp',
  }
}

export default function ProductAndBundlePageTemplate({
  pageData,
  pathname,
  serverSideFeatureFlags,
  zipCode,
}: PageTemplateProps) {
  const router = useRouter()
  // Derive the URL query string for PL request params. On the `[...slug]` route `router.query` also
  // carries the catch-all path segments under `slug`; drop that key so only the real query
  // (color / size / gender / …) is forwarded — the server builds the initial-load body the same way.
  const queryWithoutSlug = new URLSearchParams(router.query as Record<string, string>)
  // eslint-disable-next-line i18next/no-literal-string -- URL query-param key, not user-facing copy
  queryWithoutSlug.delete('slug')
  const searchParams = queryWithoutSlug.toString()

  const pageDataJson = pageData.context.pageDataJson
  const isBundle = 'bundle' in pageDataJson
  const pageDataContext = (pageData as unknown as ProductAndBundlePageData).context
  const isPLFormat = pageDataContext.isPLFormat === true

  const productFeatureFlags =
    serverSideFeatureFlags?.featureFlagType === FEATURE_FLAG_TYPE.PRODUCT ? serverSideFeatureFlags : null

  // PL path: render via the PL renderer whenever the server flags this page as PL format.
  if (isPLFormat) {
    const plData = pageDataJson as unknown as PLAPIResponse
    const pageMeta = buildPageMetaForPL(plData, pathname, searchParams, zipCode)
    const jsonLd = (pageData.context.seoData as { jsonLd?: string } | undefined)?.jsonLd
    const seoSource = (pageData.context.seoData as { metaSchemaSource?: 'pl-jsonld' } | undefined)?.metaSchemaSource
    return (
      <ProductFeatureProvider serverSideFeatureFlags={productFeatureFlags}>
        <ProductPagePLRenderer
          plData={plData}
          pageMeta={pageMeta}
          serverSideFeatureFlags={productFeatureFlags}
          jsonLd={jsonLd}
          seoSource={seoSource}
        />
      </ProductFeatureProvider>
    )
  }

  const ProductPageComponent = isBundle ? BundlePage : ProductPage
  const productPageProps = isBundle
    ? getBundlePageContext(pageDataJson)
    : getProductPageContext(pageDataJson as ProductPageBFFResponse)

  return (
    <ProductFeatureProvider serverSideFeatureFlags={productFeatureFlags}>
      <ProductPageComponent
        key={isBundle ? pageDataJson.bundle.id : (pageDataJson as ProductPageBFFResponse).product.id}
        pageData={productPageProps}
      />
    </ProductFeatureProvider>
  )
}
